/**
 * Modal for plugins that require some additional instructions when run
 *
 * Type definitions for this file:
 *
 * @typedef {import("react").ReactNode} ReactNode
 * @typedef {import("react").RefObject} RefObject
 *
 * @typedef {object} UserTextInputFieldsProps
 * @property {() => void} onDismiss
 * @property {(userTextInput: string) => void} onSubmit
 * @property {ReactNode} instructions
 * @property {string} userTextInput
 * @property {number} minWords
 * @property {number} maxWords
 *
 * @typedef {UserTextInputFieldsProps} UserTextInputPluginFormProps
 * @property {RefObject} triggerRef A ref of the element that launched the popover
 * @property {object} plugin
 * @property {string} plugin.title
 * @property {string} plugin.instructions
 * @property {number} [plugin.minWords]
 * @property {number} [plugin.maxWords]
 *
 * @typedef {object} PluginPopover
 * @property {object} triggerProps - Props to spread into the button that triggers the popover
 * @property {(plugin: object) => void} activate - Open the plugin popover with this plugin definition
 * @property {ReactNode} element - The popover to render in the component tree
 */

import React from "react";

import { useButton } from "@react-aria/button";
import { DismissButton, Overlay, useOverlayTrigger, usePopover } from "@react-aria/overlays";
import Textarea from "react-textarea-autosize";
import { SudoTooltip } from "src/sudokit/components/SudoTooltip/index.tsx";
import styled from "styled-components/macro";
import { useCommandEnterFormSubmission } from "../../../hooks/useCommandEnterFormSubmission";
import { useAppStore } from "../../../stores/appStore";
import { countWords } from "../../../Utils";
import { Button } from "../../Button";
import IconButton from "../../IconButton";
import { KeybardShortcutIcon } from "../../KeyboardShortcutIcon";

/**
 * Activate the user_text_input plugin popover anchored against a target element
 *
 * @returns {PluginPopover}
 */
export function usePluginPopover() {
  const triggerWrapperRef = React.useRef(null);
  const popoverAnchorRef = React.useRef(null);
  const [activePlugin, setActivePlugin] = React.useState(null);

  const overlayState = {
    isOpen: Boolean(activePlugin),
    close() {
      setActivePlugin(null);
    },
  };
  const overlay = useOverlayTrigger({ type: "dialog" }, overlayState, triggerWrapperRef);
  const triggerButton = useButton(overlay.triggerProps, triggerWrapperRef);

  const element = overlayState.isOpen ? (
    <UserTextInputPluginForm
      triggerRef={popoverAnchorRef}
      state={overlayState}
      overlayProps={overlay.overlayProps}
      plugin={activePlugin}
    />
  ) : null;

  return {
    triggerProps: {
      ...triggerButton.buttonProps,
      ref: triggerWrapperRef,
    },
    activate: (plugin, anchorElement) => {
      // If plugin is in the main menu, we pass the ref of the specific button,
      // so the popover appears below it.
      // Otherwise we use the triggerWrapperRef (MoreMenu wrapper)
      popoverAnchorRef.current = anchorElement || triggerWrapperRef.current;
      setActivePlugin(plugin);
    },
    element,
  };
}

/**
 * Renders the UserTextInputPluginFields inside an overlay - absolutely
 * positioned relative to a trigger element
 *
 * @param {UserTextInputPluginFormProps} props
 */
export function UserTextInputPluginForm(props) {
  const popoverRef = React.useRef(null);
  const { popoverProps, underlayProps } = usePopover(
    {
      triggerRef: props.triggerRef,
      popoverRef,
      placement: "bottom start",
      offset: 10,
    },
    props.state
  );

  function handleSubmit(formData) {
    const input = formData.get("user-text-input");
    props.plugin.onSubmit(input);
    props.state.close();
  }

  return (
    <Overlay>
      <Underlay {...underlayProps} />
      <Wrapper
        {...popoverProps}
        ref={popoverRef}
      >
        <DismissButton onDismiss={props.state.close} />
        <Form
          onSubmit={handleSubmit}
          {...props.overlayProps}
        >
          <UserTextInputFields
            {...props.plugin}
            onDismiss={props.state.close}
          />
        </Form>
        <DismissButton onDismiss={props.state.close} />
      </Wrapper>
    </Overlay>
  );
}

const Underlay = styled.div`
  position: fixed;
  inset: 0;
`;

const Wrapper = styled.div`
  background-color: var(--menu-color);
  border-radius: 20px;
  box-shadow: var(--default-drop-shadow);
`;

function Form({ onSubmit, ...props }) {
  const formRef = React.useRef(null);
  useCommandEnterFormSubmission(formRef);

  function handleSubmit(event) {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    onSubmit(formData);
  }

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit}
      {...props}
    />
  );
}

function pluralize(count, noun, multiple) {
  return count === 1 ? noun : multiple ? multiple : noun + "s";
}

/**
 * @param {UserTextInputFieldsProps} props
 */
export function UserTextInputFields(props) {
  const placeholder = props.placeholder || "Type here";
  const [userTextInput, setUserTextInput] = React.useState(props.userTextInput || "");
  const wordCount = countWords(userTextInput);
  const isMac = useAppStore((state) => state.isMac);
  const textareaRef = React.useRef(null);

  // Focus the textarea on mount without scrolling the page.
  // Using autoFocus causes mobile browsers to scroll-to-focus on the
  // portaled overlay, which pushes the relatively-positioned toolbar
  // off the top of the visual viewport. preventScroll avoids this.
  React.useEffect(() => {
    if (textareaRef.current && !props.disabled) {
      textareaRef.current.focus({ preventScroll: true });
    }
  }, []);

  let errorMessage = null;

  if (props.minWords && wordCount < props.minWords) {
    errorMessage = `This plugin requires at least ${props.minWords} ${pluralize(props.minWords, "word")} to run`;
  } else if (props.maxWords && wordCount > props.maxWords) {
    errorMessage = `This plugin can't accept higher than ${props.maxWords} words`;
  }

  return (
    <PluginFieldset>
      <PluginName>{props.name}</PluginName>
      <PluginLabel htmlFor="user-text-input-plugin-textarea">{props.instructions}</PluginLabel>
      {props.onDismiss && <DismissPlugin onClick={props.onDismiss} />}
      <PluginTextarea
        ref={textareaRef}
        id="user-text-input-plugin-textarea"
        name="user-text-input"
        placeholder={placeholder}
        value={userTextInput}
        onChange={(e) => setUserTextInput(e.target.value)}
        disabled={props.disabled}
      />
      <PluginButton
        disabled={props.disabled}
        hoverTipText={errorMessage}
      >
        Go&nbsp;
        <KeybardShortcutIcon name={isMac ? "command" : "control"} />
        <KeybardShortcutIcon name="enter" />
      </PluginButton>
    </PluginFieldset>
  );
}

export const DismissPlugin = styled(IconButton).attrs({ src: "/assets/close.svg" })`
  grid-area: close;
  align-self: start;
  margin: -14px -10px;
`;

export const PluginFieldset = styled.fieldset`
  max-width: 400px;
  padding: 15px;
  display: grid;
  gap: 8px 12px;
  grid-template-areas:
    "name name close"
    "instructions instructions instructions"
    "input button button";
  grid-template-columns: 1fr auto auto;
  align-items: center;
`;

const PluginName = styled.h1`
  color: var(--grey-1);
  margin: 0 !important;
  font-size: inherit;
  grid-area: name;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  line-height: 1;
  opacity: 50%;
  font-size: 14px;
  font-weight: 600;
`;

const PluginLabel = styled.label`
  grid-area: instructions;
  text-align: initial;
  color: var(--grey-0);
  font-weight: 600;
  line-height: 1.2;
  margin-bottom: 10px !important;
`;

const PluginTextarea = styled(Textarea)`
  border: none !important;
  padding: 0 !important;
  margin: 0 !important;

  grid-area: input;
  display: block;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.3;
  background: transparent;
  color: var(--grey-0);
  resize: none;
  min-height: 1.3em;
  max-height: 500px;
  overflow-y: auto;
`;

// There's some button styling for menu items that we need to disable here
const PluginButton = styled(UnstyledPluginButton)`
  grid-area: button;
  align-self: end;
  position: relative;

  button {
    background: none !important;
    margin: 0 !important;
    padding: 8px 10px !important;

    &:hover {
      background: none !important;
    }

    > svg {
      height: 20px !important;
    }
  }

  .Tooltip {
    top: 50px;
  }
`;

function UnstyledPluginButton({ disabled, hoverTipText, children, ...props }) {
  let contents = (
    <Button
      primary
      type="submit"
      disabled={disabled || hoverTipText}
    >
      {children}
    </Button>
  );

  if (hoverTipText) {
    contents = (
      <SudoTooltip
        tooltipClassName='w-[8em] max-w-[90vw]'
        text={hoverTipText}
      >
        {contents}
      </SudoTooltip>
    );
  }

  return <div {...props}>{contents}</div>;
}
